import type { LucideIcon } from "lucide-react-native";
import { Pressable, Text, View } from "react-native";
import { EMBER } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type CompactIconTabOption<Value extends string> = {
  value: Value;
  label: string;
  icon: LucideIcon;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
};

type Props<Value extends string> = {
  options: readonly CompactIconTabOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  /** Use the denser sizing intended for narrow section headers. */
  compact?: boolean;
  /** Show every option's label, for headers with room to spare. */
  showLabels?: boolean;
};

/**
 * Compact segmented control for in-card views: inactive options keep only
 * their icon, while the selected option expands to show its label. With
 * `showLabels`, every option shows its label.
 */
export function CompactIconTabs<Value extends string>({
  options,
  value,
  onChange,
  compact = false,
  showLabels = false,
}: Props<Value>) {
  const capturePress = usePressCapture("CompactIconTabs");
  return (
    <View
      style={{
        flexDirection: "row",
        borderRadius: 999,
        padding: 3,
        backgroundColor: "rgba(255,255,255,0.34)",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.5)",
        flexShrink: 0,
      }}
    >
      {options.map((option) => {
        const selected = value === option.value;
        const labelled = selected || showLabels;
        const Icon = option.icon;

        return (
          <Pressable
            key={option.value}
            onPress={() => {
              capturePress(
                pressLabel(option.label, option.accessibilityLabel),
                option.action ?? option.value,
              );
              onChange(option.value);
            }}
            accessibilityRole="button"
            accessibilityLabel={option.accessibilityLabel ?? option.label}
            accessibilityState={{ selected }}
            style={({ pressed }) => ({
              minHeight: compact ? 28 : 30,
              flexDirection: "row",
              alignItems: "center",
              gap: labelled ? (compact ? 4 : 6) : 0,
              borderRadius: 999,
              paddingHorizontal: compact ? 8 : 11,
              backgroundColor: selected
                ? "rgba(255,255,255,0.74)"
                : pressed
                  ? "rgba(255,255,255,0.34)"
                  : "transparent",
            })}
          >
            <Icon
              size={compact ? 12 : 13}
              color={selected ? EMBER.colors.textMid : EMBER.colors.textLight}
              strokeWidth={1.8}
            />
            {labelled ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: compact ? 11 : 12,
                  lineHeight: 15,
                  color: selected
                    ? EMBER.colors.textMid
                    : EMBER.colors.textLight,
                  fontWeight: selected ? "800" : "600",
                }}
              >
                {option.label}
              </Text>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}
